<template>
  <div class="product-card-prices">
    <span v-if="!product.available" class="product-card-prices__sold">Sold Out</span>

    <template v-else-if="hasRange">
      <div class="product-card-prices__main">
        <span class="product-card-prices__from">From</span>
        <ShopifyPrice :amount="priceMin" />
        <span class="product-card-prices__dash">—</span>
        <ShopifyPrice :amount="priceMax" />
      </div>

      <div v-if="showChainSuffix" class="product-card-prices__suffix">(+ Chain)</div>
      <div v-if="isDsmExclusive" class="product-card-prices__dsm">*(DSM Exclusive)</div>
    </template>

    <template v-else>
      <div class="product-card-prices__main">
        <ShopifyPrice class="product-card-prices__price" :amount="priceMin" />
      </div>

      <div v-if="showChainSuffix" class="product-card-prices__suffix">(+ Chain)</div>
      <div v-if="isDsmExclusive" class="product-card-prices__dsm">*(DSM Exclusive)</div>
    </template>

    <ShopifyPrice
      class="product-card-prices__compare"
      v-if="product.compare_at_price"
      :amount="parse(product.compare_at_price)"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { LiquidProduct } from '../../../types'
import ShopifyPrice from '../../shopify/ShopifyPrice.vue'

const props = defineProps<{ product: LiquidProduct }>()

const parse = (value: string) => parseFloat(value) / 100

const priceMin = computed(() => parse(props.product.price_min))
const priceMax = computed(() => parse(props.product.price_max ?? props.product.price_min))

const hasRange = computed(() => Boolean(props.product.price_varies) && priceMax.value > priceMin.value)

const showChainSuffix = computed(() => {
  const p: any = props.product
  const chainEnabled = Boolean(p.chainEnabled?.value ?? p.chainEnabled ?? false)

  const tags: string[] = Array.isArray((p as any).tags) ? (p as any).tags : []
  const hasTag = tags.some((t) => String(t).toLowerCase() === 'chain enabled')

  return chainEnabled || hasTag
})

const isDsmExclusive = computed(() => {
  const p: any = props.product
  const tags: string[] = Array.isArray(p.tags) ? p.tags : []

  return tags.some((tag) => String(tag).toLowerCase() === 'dsm-hide')
})
</script>

<style lang="scss">
@import '~styles/base';

.product-card-prices {
  display: flex;
  flex-direction: column;
  vertical-align: baseline;
  transition: var(--transition-intersection, var(--transition-opacity));
  gap: 0px;

  &__main {
    display: flex;
    align-items: baseline;
  }

  @media (min-width: 1001px) {
    &__main {
      justify-content: flex-start;
    }
  }

  &__from {
    margin-right: 4px;
    text-transform: capitalize !important;
  }

  &__dash {
    margin: 0 4px;
  }

  &__dsm {
    white-space: nowrap;
  }

  &__suffix {
    white-space: nowrap;
    text-transform: capitalize !important;
  }

  &__compare {
    margin-left: 5px;
    text-decoration: line-through;
  }

  @media (min-width: 1001px) {
    text-align: left;
  }

  @include breakpoint('xl') {
    margin-top: 10px;
  }
}
</style>
